import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { useWallet } from '@/hooks/useWallet.ts'
import { useTermsConsent } from '@/hooks/useTermsConsent.ts'
import { TermsCheckbox } from '@/components/ui/TermsCheckbox/index.ts'
import { cn } from '@/utils/cn.ts'

export function PricingSection() {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const { address } = useWallet()
  const [acceptedTerms, setAcceptedTerms] = useState(() => true)
  const { record: recordConsent } = useTermsConsent()

  const plans = [
    {
      name: t('pricing.plan1.name'),
      price: t('pricing.plan1.price'),
      period: t('pricing.plan1.period'),
      promo: {
        regularPrice: t('pricing.plan1.priceRegular'),
        badge: t('pricing.plan1.promoBadge'),
      },
      features: [
        t('pricing.plan1.feature1'),
        t('pricing.plan1.feature2'),
        t('pricing.plan1.feature3'),
        t('pricing.plan1.feature4'),
      ],
      highlighted: false,
    },
    {
      name: t('pricing.plan2.name'),
      price: t('pricing.plan2.price'),
      period: t('pricing.plan2.period'),
      promo: undefined,
      features: [
        t('pricing.plan2.feature1'),
        t('pricing.plan2.feature2'),
        t('pricing.plan2.feature3'),
        t('pricing.plan2.feature4'),
        t('pricing.plan2.feature5'),
      ],
      highlighted: true,
    },
  ]

  const handleGetStarted = () => {
    if (!acceptedTerms) return
    void recordConsent({
      source: 'pricing_get_started',
      walletAddress: address ?? null,
      language: i18n.language,
    })
    navigate('/onboarding/benefactor')
  }

  const ctaDisabledClass = !acceptedTerms
    ? 'cursor-not-allowed opacity-50'
    : 'cursor-pointer'

  return (
    <section id="pricing" className="px-4 py-16 max-md:py-12 md:px-8 md:py-24">
      <div className="max-w-content mx-auto text-center">
        <span className="border-accent/20 bg-accent-muted text-accent mb-6 inline-block rounded-full border px-4 py-1.5 text-xs font-semibold tracking-widest uppercase">
          {t('pricing.badge')}
        </span>
        <h2 className="text-fg mb-2 text-[clamp(1.375rem,3vw,2.25rem)] font-bold">
          {t('pricing.heading')}
        </h2>
        <p className="text-fg-secondary mx-auto mb-12 max-w-[540px] text-base max-md:mb-8">
          {t('pricing.subheading')}
        </p>

        <div className="mx-auto grid max-w-[800px] grid-cols-[repeat(auto-fit,minmax(min(100%,300px),1fr))] gap-8">
          {plans.map((plan) => (
            <div
              key={plan.name}
              data-testid="pricing-plan"
              className={cn(
                'border-line bg-gradient-card duration-base relative rounded-xl border p-8 text-left transition-all hover:-translate-y-1 hover:shadow-lg max-md:p-6',
                plan.highlighted &&
                  'hover:shadow-glow-lg border-[rgba(201,168,76,0.3)] shadow-[0_0_30px_rgba(201,168,76,0.08)]',
              )}
            >
              {plan.highlighted && (
                <span className="bg-gradient-brand text-fg-inverse absolute -top-3 right-6 rounded-full px-3 py-1 text-[0.6875rem] font-bold tracking-wide uppercase">
                  {t('pricing.popular')}
                </span>
              )}
              <h3 className="text-fg mb-4 text-lg font-semibold">
                {plan.name}
              </h3>
              <div className="mb-8 flex flex-col gap-2 text-left">
                {plan.promo && (
                  <span className="text-accent bg-accent-muted/35 w-fit rounded-md px-2 py-1 text-[0.625rem] font-bold tracking-wider uppercase">
                    {plan.promo.badge}
                  </span>
                )}
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  {plan.promo && (
                    <span className="text-fg-muted decoration-fg-muted/50 text-base font-semibold line-through">
                      {plan.promo.regularPrice}
                    </span>
                  )}
                  <span className="text-fg text-[clamp(2rem,5vw,2.5rem)] font-extrabold tracking-tight">
                    {plan.price}
                  </span>
                  <span className="text-fg-muted text-sm">/{plan.period}</span>
                </div>
              </div>
              <ul className="mb-8 flex flex-col gap-2">
                {plan.features.map((feature) => (
                  <li
                    key={feature}
                    className="text-fg-secondary flex items-center gap-2 text-sm"
                  >
                    <span className="text-accent shrink-0 text-sm font-bold">
                      &#10003;
                    </span>
                    {feature}
                  </li>
                ))}
              </ul>
              <button
                type="button"
                disabled={!acceptedTerms}
                aria-disabled={!acceptedTerms}
                title={!acceptedTerms ? t('terms.requiredHint') : undefined}
                className={cn(
                  'duration-base w-full rounded-lg py-3 text-[0.9375rem] font-semibold transition-all',
                  plan.highlighted
                    ? 'bg-gradient-brand text-fg-inverse shadow-glow hover:shadow-glow-lg border-none'
                    : 'border-line text-fg hover:border-accent hover:text-accent border bg-transparent',
                  ctaDisabledClass,
                )}
                onClick={handleGetStarted}
              >
                {t('pricing.getStarted')}
              </button>
            </div>
          ))}
        </div>

        <TermsCheckbox
          id="pricing-terms"
          checked={acceptedTerms}
          onChange={setAcceptedTerms}
          showRequiredHint
          className="mt-8"
        />
      </div>
    </section>
  )
}
